import { useEffect } from 'react'
import { createLogger } from '@/lib/logger'
import { isTauri } from '@/lib/utils/tauri'
import { resetPersistedModuleFilters } from '@/store/filter-scope'

const logger = createLogger('hooks/useDesktopAccountTransition')

export const DESKTOP_ACCOUNT_TRANSITION_EVENT =
  'desktop-account-transition' as const

export interface DesktopAccountTransitionPayload {
  destination: '/organizations' | '/login?addAccount=true' | '/login'
}

/**
 * Receives account changes made in the dedicated Settings webview and reloads
 * the main webview against the newly selected desktop credentials.
 */
export function useDesktopAccountTransition(): void {
  useEffect(() => {
    if (!isTauri()) return

    let cancelled = false
    let unlisten: (() => void) | undefined

    void import('@tauri-apps/api/event')
      .then(({ listen }) =>
        listen<DesktopAccountTransitionPayload>(
          DESKTOP_ACCOUNT_TRANSITION_EVENT,
          event => {
            const destination = event.payload?.destination
            if (
              destination !== '/organizations' &&
              destination !== '/login?addAccount=true' &&
              destination !== '/login'
            ) {
              logger.warn('Ignoring invalid desktop account destination')
              return
            }

            resetPersistedModuleFilters()
            localStorage.removeItem('currentOrganizationId')
            window.location.href = destination
          },
        ),
      )
      .then(listener => {
        if (cancelled) {
          listener()
        } else {
          unlisten = listener
        }
      })
      .catch(error => {
        logger.error('Failed to listen for desktop account changes:', error)
      })

    return () => {
      cancelled = true
      unlisten?.()
    }
  }, [])
}
